<template>
    <div class="trends-chart">
        <div class="trends-chart__head">发文趋势</div>

        <div class="trends-chart__toolbar">
            <div class="trends-chart__year-range">
                <el-date-picker
                    v-model="yearStart"
                    type="year"
                    value-format="yyyy"
                    format="yyyy年"
                    placeholder="起始年"
                    :clearable="false"
                    size="small"
                    class="trends-chart__year-picker"
                />
                <span class="trends-chart__year-sep">-</span>
                <el-date-picker
                    v-model="yearEnd"
                    type="year"
                    value-format="yyyy"
                    format="yyyy年"
                    placeholder="结束年"
                    :clearable="false"
                    size="small"
                    class="trends-chart__year-picker"
                />
                <el-button type="primary" size="small" class="trends-chart__submit" @click="onSubmit">
                    提交
                </el-button>
            </div>
            <div
                class="trends-chart__legend"
                :class="{ 'is-hover': legendHover }"
                @mouseenter="onLegendEnter"
                @mouseleave="onLegendLeave"
            >
                <span class="trends-chart__legend-icon"></span>
                <span class="trends-chart__legend-text">{{ legendName }}</span>
            </div>
            <div class="trends-chart__y-label">发文量(篇)</div>
        </div>

        <div ref="chartRef" class="trends-chart__canvas"></div>

        <div class="trends-chart__summary">
            <span class="trends-chart__summary-label">图解：</span>
            <span class="trends-chart__summary-text">
                <em>{{ summary.keyword }}</em>检索得到
                <em>{{ summary.dataVersion }}</em>数据
                <em>{{ summary.total }}</em>条，起始于
                <em>{{ summary.startYear }}</em>年，
                <em>{{ summary.peakYear }}</em>年达到最大值。
            </span>
        </div>
    </div>
</template>

<script>
import defaultTrendsData from '../trendsChartData';

const CHART_COLOR = '#396de7';

export default {
    name: 'TrendsChart',
    props: {
        /** 接口返回的发文趋势数据，结构与 trendsChartData.js 一致 */
        chartData: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            yearStart: '',
            yearEnd: '',
            chart: null,
            innerData: null,
            legendHover: false,
        };
    },
    computed: {
        sourceData() {
            return this.chartData && this.chartData.trendData
                ? this.chartData
                : defaultTrendsData;
        },
        legendName() {
            const d = this.innerData || this.sourceData;
            return d.legendName || (d.trendData.series[0] && d.trendData.series[0].name) || '';
        },
        summary() {
            const d = this.innerData || this.sourceData;
            return {
                keyword: d.keyword || '',
                dataVersion: d.dataVersion || '',
                total: d.total != null ? d.total : 0,
                startYear: d.startYear || '',
                peakYear: d.peakYear || '',
            };
        },
    },
    watch: {
        chartData: {
            handler() {
                this.applySourceData();
            },
            deep: true,
        },
    },
    mounted() {
        this.applySourceData();
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        applySourceData() {
            this.innerData = JSON.parse(JSON.stringify(this.sourceData));
            this.yearStart = String(this.innerData.startYear || this.innerData.trendData.xAxis[0]);
            this.yearEnd = String(this.innerData.endYear || this.innerData.trendData.xAxis.slice(-1)[0]);
            this.$nextTick(() => {
                this.initChart();
                this.renderChart();
            });
        },
        initChart() {
            if (this.chart || !this.$refs.chartRef) return;
            this.chart = this.$echarts.init(this.$refs.chartRef);
        },
        getFilteredTrend() {
            const d = this.innerData || this.sourceData;
            const start = Number(this.yearStart);
            const end = Number(this.yearEnd);
            const xAxis = [];
            const data = [];
            const series0 = d.trendData.series[0] || { data: [] };
            d.trendData.xAxis.forEach((year, i) => {
                const y = Number(year);
                if (y >= start && y <= end) {
                    xAxis.push(year);
                    data.push(series0.data[i]);
                }
            });
            return { xAxis, data, name: series0.name || d.legendName };
        },
        getPointStyle(solid) {
            if (solid) {
                return {
                    color: CHART_COLOR,
                    borderColor: CHART_COLOR,
                    borderWidth: 0,
                };
            }
            return {
                color: '#fff',
                borderColor: CHART_COLOR,
                borderWidth: 2,
            };
        },
        buildOption() {
            const { xAxis, data, name } = this.getFilteredTrend();
            const maxVal = data.length ? Math.max(...data) : 40;
            const yMax = Math.ceil(maxVal / 10) * 10 || 10;

            return {
                color: [CHART_COLOR],
                grid: {
                    left: 48,
                    right: 36,
                    top: 16,
                    bottom: 72,
                },
                tooltip: {
                    trigger: 'axis',
                    formatter(params) {
                        const p = params[0];
                        if (!p) return '';
                        return `${p.axisValue}年<br/>${p.seriesName}：${p.value}篇`;
                    },
                },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: xAxis,
                    axisLine: {
                        lineStyle: { color: '#ccc' },
                    },
                    axisTick: { show: false },
                    axisLabel: {
                        color: '#666',
                        interval: (index) => index % 2 === 0,
                        formatter: (val, index) => {
                            if (index === xAxis.length - 1) return `${val}年`;
                            return val;
                        },
                    },
                },
                yAxis: {
                    type: 'value',
                    min: 0,
                    max: yMax,
                    interval: yMax / 4,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { color: '#666' },
                    splitLine: {
                        lineStyle: { color: '#e8e8e8' },
                    },
                },
                dataZoom: [
                    {
                        type: 'slider',
                        show: true,
                        xAxisIndex: 0,
                        height: 28,
                        bottom: 8,
                        borderColor: 'transparent',
                        backgroundColor: '#f5f7fa',
                        fillerColor: 'rgba(57, 109, 231, 0.15)',
                        handleStyle: {
                            color: '#d9dce3',
                            borderColor: '#c0c4cc',
                        },
                        dataBackground: {
                            lineStyle: { color: CHART_COLOR, opacity: 0.35 },
                            areaStyle: { color: CHART_COLOR, opacity: 0.12 },
                        },
                        selectedDataBackground: {
                            lineStyle: { color: CHART_COLOR, opacity: 0.6 },
                            areaStyle: { color: CHART_COLOR, opacity: 0.2 },
                        },
                        textStyle: { color: '#999' },
                    },
                ],
                series: [
                    {
                        name,
                        type: 'line',
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: this.legendHover ? 8 : 7,
                        showSymbol: true,
                        lineStyle: {
                            width: this.legendHover ? 3 : 2,
                            color: CHART_COLOR,
                        },
                        itemStyle: this.getPointStyle(this.legendHover),
                        areaStyle: {
                            color: {
                                type: 'linear',
                                x: 0,
                                y: 0,
                                x2: 0,
                                y2: 1,
                                colorStops: [
                                    { offset: 0, color: 'rgba(57, 109, 231, 0.35)' },
                                    { offset: 1, color: 'rgba(57, 109, 231, 0.05)' },
                                ],
                            },
                        },
                        data,
                    },
                ],
            };
        },
        renderChart() {
            this.initChart();
            if (!this.chart) return;
            this.chart.setOption(this.buildOption(), true);
        },
        onSubmit() {
            const start = Number(this.yearStart);
            const end = Number(this.yearEnd);
            if (!start || !end || start > end) {
                this.$message.warning('请选择有效的年份范围');
                return;
            }
            this.renderChart();
        },
        handleResize() {
            if (this.chart) this.chart.resize();
        },
        updatePointStyle() {
            if (!this.chart) return;
            const solid = this.legendHover;
            this.chart.setOption({
                series: [{
                    symbolSize: solid ? 8 : 7,
                    lineStyle: { width: solid ? 3 : 2 },
                    itemStyle: this.getPointStyle(solid),
                }],
            });
        },
        onLegendEnter() {
            this.legendHover = true;
            this.updatePointStyle();
        },
        onLegendLeave() {
            this.legendHover = false;
            this.updatePointStyle();
        },
    },
};
</script>

<style lang="less" scoped>
.trends-chart {
    background: #fff;
    // border: 1px solid #e4e7ed;
    padding: 10px 20px ;
    box-sizing: border-box;

    &__head {
        margin-bottom: 16px;
        font-size: 16px;
        font-weight: bold;
        color: #333; 
        line-height: 32px;
        border-bottom: 2px solid #396de7;
        padding-bottom: 6px;
    }   

    &__toolbar {
        margin-bottom: 8px;
    }

    &__year-range {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
    }

    &__year-picker {
        width: 120px;
    }

    &__year-sep {
        margin: 0 8px;
        color: #666;
    }

    &__submit {
        margin-left: 12px;
        background-color: #396de7;
        border-color: #396de7;
    }

    &__legend {
        display: inline-flex;
        align-items: center;
        margin-bottom: 6px;
        padding: 4px 10px 4px 6px;
        margin-left: -6px;
        border-radius: 4px;
        cursor: pointer;
        user-select: none;
        transition: background 0.2s ease;

        &:hover,
        &.is-hover {
            background: rgba(57, 109, 231, 0.08);

            .trends-chart__legend-icon::before {
                background: #2859c5;
            }

            .trends-chart__legend-icon::after {
                border-color: #2859c5;
                background: #396de7;
                box-shadow: none;
            }

            .trends-chart__legend-text {
                color: #396de7;
                font-weight: 600;
            }
        }
    }

    &__legend-icon {
        position: relative;
        width: 28px;
        height: 14px;
        margin-right: 6px;
        flex-shrink: 0;

        &::before {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            top: 50%;
            height: 2px;
            margin-top: -1px;
            background: #396de7;
            transition: background 0.2s ease;
        }

        &::after {
            content: '';
            position: absolute;
            left: 50%;
            top: 50%;
            width: 10px;
            height: 10px;
            margin: -5px 0 0 -5px;
            border-radius: 50%;
            border: 2px solid #396de7;
            background: #fff;
            box-sizing: border-box;
            transition: border-color 0.2s ease, background 0.2s ease;
        }
    }

    &__legend-text {
        font-size: 14px;
        color: #333;
        transition: color 0.2s ease, font-weight 0.2s ease;
    }

    &__y-label {
        font-size: 14px;
        color: #666;
        margin-bottom: 4px;
    }

    &__canvas {
        width: 100%;
        height: 320px;
    }

    &__summary {
        margin-top: 12px;
        font-size: 14px;
        line-height: 1.8;
        color: #666;
    }

    &__summary-label {
        color: #333;
        font-style: normal;
    }

    &__summary-text em {
        font-style: normal;
        color: #396de7;
        font-weight: bold;
    }
}
</style>
